<script setup lang="ts">
import { computed } from 'vue';
import Toast from 'primevue/toast';
import { Stack, Typography } from '@apparatix/library/core';
import type { OfferSubmissionDocument, OfferSubmissionEligibility, OfferSubmissionMode } from '@apparatix/types/Documents';
import OfferSubmissionForm from './OfferSubmission/OfferSubmissionForm.vue';
import { documentSubtitle, formCopy } from './OfferSubmission/offerSubmissionCopy';

/**
 * Solution Center host for the Submit Offer / Submit Contract form. Rendered
 * by resources/views/proposal/proposal-submit-offer.blade.php inside the
 * proposal tool's mySlider16 iframe, so completion is reported back to the
 * parent frame the same way ProposalEvaluateRate does.
 */
type Props = {
  proposalId: number;
  mode: OfferSubmissionMode;
  displayId: string;
  entityName: string;
  offerSubmission: OfferSubmissionEligibility;
};

type LegacyProposalToolWindow = Window & {
  hideLegacyModalWindow?: (sliderId: string) => void;
  onOfferSubmissionComplete?: (mode: OfferSubmissionMode) => void;
};

const SLIDER_ID = 'mySlider16';

const props = defineProps<Props>();

const parentWindow = () => window.parent as LegacyProposalToolWindow;

const title = computed(() => formCopy(props.mode, props.offerSubmission.isEssentials).title);
const document = computed<OfferSubmissionDocument>(() => ({
  proposalId: Number(props.proposalId),
  displayId: String(props.displayId),
  entityName: props.entityName,
}));
const subtitle = computed(() => documentSubtitle(document.value));

const onCancel = () => parentWindow().hideLegacyModalWindow?.(SLIDER_ID);

const onSubmitted = () => {
  const parent = parentWindow();
  if (parent.onOfferSubmissionComplete) {
    parent.onOfferSubmissionComplete(props.mode);
  } else {
    parent.hideLegacyModalWindow?.(SLIDER_ID);
  }
};
</script>

<template>
  <div v-cloak class="max-w-2xl mx-auto py-4 px-2">
    <!-- Blade islands render their own outlet: the slide-in iframe has no App.vue shell -->
    <Toast />
    <Stack :gap="0" class="rounded-lg bg-white shadow-[0px_1px_3px_rgba(0,0,0,0.10)]">
      <Stack :gap="1" class="p-4 border-b border-[var(--p-content-border-color)]">
        <Typography variant="dialog-heading">
          {{ title }}
        </Typography>
        <Typography variant="small-body" muted>
          {{ subtitle }}
        </Typography>
      </Stack>
      <Typography v-if="!props.offerSubmission.isDocumentEligible" variant="body" class="p-4">
        This document is not eligible: it needs an offer number and must not already have a contract.
      </Typography>
      <OfferSubmissionForm
        v-else
        :proposal-id="document.proposalId"
        :mode="props.mode"
        :is-essentials="props.offerSubmission.isEssentials"
        @cancel="onCancel"
        @submitted="onSubmitted"
      />
    </Stack>
  </div>
</template>
